import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpcClient } from '@/utils/trpc';

type PushNotificationState = {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  permission: NotificationPermission;
};

export function usePushNotifications() {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isSubscribed: false,
    isLoading: true,
    permission: 'default',
  });

  const [subscription, setSubscription] = useState<PushSubscription | null>(
    null
  );

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    try {
      // Check if service workers and push notifications are supported
      const isSupported =
        'serviceWorker' in navigator && 'PushManager' in window;
      const permission = Notification.permission;

      if (isSupported) {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered:', registration);

        // Check if already subscribed
        const existingSubscription =
          await registration.pushManager.getSubscription();
        setSubscription(existingSubscription);

        setState({
          isSupported: true,
          isSubscribed: !!existingSubscription,
          isLoading: false,
          permission,
        });
      } else {
        setState({
          isSupported: false,
          isSubscribed: false,
          isLoading: false,
          permission,
        });
      }
    } catch (error) {
      console.error('Error checking push notification support:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const requestPermission = async (): Promise<NotificationPermission> => {
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setState((prev) => ({ ...prev, permission }));
      return permission;
    }
    return Notification.permission;
  };

  const subscribe = async (): Promise<boolean> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      // Request permission if needed
      const permission = await requestPermission();
      if (permission !== 'granted') {
        toast.error('Permissão para notificações negada');
        setState((prev) => ({ ...prev, isLoading: false }));
        return false;
      }

      // Get VAPID public key
      const { publicKey } =
        await trpcClient.notifications.getVapidPublicKey.query();

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const newSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // Send subscription to server
      await trpcClient.notifications.subscribe.mutate({
        endpoint: newSubscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(newSubscription.getKey('p256dh')),
          auth: arrayBufferToBase64(newSubscription.getKey('auth')),
        },
        userAgent: navigator.userAgent,
      });

      setSubscription(newSubscription);
      setState((prev) => ({
        ...prev,
        isSubscribed: true,
        isLoading: false,
        permission,
      }));

      toast.success('Notificações ativadas com sucesso!');
      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      toast.error('Erro ao ativar notificações');
      setState((prev) => ({ ...prev, isLoading: false }));
      return false;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from server
        await trpcClient.notifications.unsubscribe.mutate({
          endpoint: subscription.endpoint,
        });

        setSubscription(null);
        setState((prev) => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
        }));

        toast.success('Notificações desativadas');
        return true;
      }

      setState((prev) => ({ ...prev, isLoading: false }));
      return false;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      toast.error('Erro ao desativar notificações');
      setState((prev) => ({ ...prev, isLoading: false }));
      return false;
    }
  };

  const sendTestNotification = async (): Promise<boolean> => {
    try {
      const result =
        await trpcClient.notifications.sendTestNotification.mutate();

      if (result.sent > 0) {
        toast.success(
          `Notificação de teste enviada! (${result.sent}/${result.total})`
        );
        return true;
      }
      toast.error('Nenhuma notificação foi enviada');
      return false;
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Erro ao enviar notificação de teste');
      return false;
    }
  };

  return {
    ...state,
    subscribe,
    unsubscribe,
    sendTestNotification,
    requestPermission,
  };
}

// Utility functions
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';

  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
}
